import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import dayjs from "dayjs";
import { CalendarEvent } from "../models/calendarEvent";
import { CalendarConnection, CalendarProvider } from "../models/user";
import {
  CalendarConnectionStatusMap,
  connectionStatusKey,
  DateRange,
  getEventUniqueKey,
  getOutlookAccountsInMsalCache,
  isGoogleTokenRevokedError,
  mergeDateRanges,
  normalizeEmail,
} from "../util/calendarEvent";
import {
  completeCalendarConnect,
  connectCalendar,
  disconnectCalendar,
  fetchGoogleCalendarEvents,
  fetchOutlookCalendarEvents,
  getCalendarErrorMessage,
  getPendingCalendarConnect,
  hasPendingCalendarConnect,
} from "../services/calendarService";
import { msGraphAuthService } from "../services/MsGraphAuthService";

// ─────────────────────────────────────────────────────────────────────
// Shared calendar events hook used by the Meetings calendar page and the
// Coaching Navigator calendar. Centralises: initial load, pagination,
// incremental fetching, search (with on-demand wider range load),
// connect / disconnect (including Outlook redirect completion),
// per-connection status map, and warning snackbars for unavailable /
// failed / revoked accounts.
//
// See .github/instructions/calendar-events.instructions.md.
// ─────────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;

export type SnackbarSeverity = "success" | "error";

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

export interface UseCalendarEventsOptions<T extends CalendarEvent> {
  /** All calendar connections for the invoker. */
  connections: CalendarConnection[];
  /** Refreshes invoker (e.g. `actions.users.getInvoker`). */
  refreshInvoker: () => Promise<unknown>;
  /** Namespace-aware mapper for Outlook events (callers wrap `mapOutlookEvent` from util). */
  mapOutlookEvent: (raw: unknown, accountUsername?: string) => T;
  /** Namespace-aware mapper for Google events. */
  mapGoogleEvent: (raw: unknown, accountUsername?: string) => T;
  /** Predicate used to filter events by a lower-cased query. */
  matchEvent: (event: T, loweredQuery: string) => boolean;
  /** i18next `t` — used for warning + status snackbars. */
  t: TFunction;
  /** How many months of past data to include in the dropdown / search range. */
  pastMonths?: number;
  /** How many months of future data to include in the dropdown / search range. */
  futureMonths?: number;
  /** Search range (used when a non-empty search query is active). */
  searchPastDays: number;
  searchFutureDays: number;
  /** Meetings page prefers to restore the app account after an Outlook redirect. */
  restoreOriginalAccountOnRedirect?: boolean;
  /** Show success/error snackbars for manual connect / disconnect actions. */
  showConnectionSnackbars?: boolean;
}

export interface UseCalendarEventsResult<T extends CalendarEvent> {
  // ── data ──────────────────────────────────────────────────────────
  events: T[];
  filteredEvents: T[];
  connectionStatuses: CalendarConnectionStatusMap;

  // ── search ────────────────────────────────────────────────────────
  searchText: string;
  setSearchText: (value: string) => void;
  debouncedSearchText: string;
  isSearchActive: boolean;
  searchInProgress: boolean;

  // ── loading / month navigation ────────────────────────────────────
  loading: boolean;
  viewedMonth: Date;
  setViewedMonth: (d: Date) => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  availableMonths: Date[];

  // ── connect / disconnect ──────────────────────────────────────────
  connectLoading: boolean;
  connectError: string | null;
  setConnectError: (value: string | null) => void;
  handleConnectCalendar: (provider: CalendarProvider, includePrivateEvents: boolean) => Promise<boolean>;
  handleDisconnectAccount: (
    provider: string,
    accountUsername: string,
  ) => Promise<void>;

  // ── actions ───────────────────────────────────────────────────────
  fetchAllEvents: () => Promise<void>;

  // ── snackbar ──────────────────────────────────────────────────────
  snackbar: SnackbarState;
  setSnackbar: React.Dispatch<React.SetStateAction<SnackbarState>>;
  closeSnackbar: () => void;
}

export function useCalendarEvents<T extends CalendarEvent>(
  options: UseCalendarEventsOptions<T>,
): UseCalendarEventsResult<T> {
  const {
    connections,
    refreshInvoker,
    mapOutlookEvent,
    mapGoogleEvent,
    matchEvent,
    t,
    pastMonths = 12,
    futureMonths = 24,
    searchPastDays,
    searchFutureDays,
    restoreOriginalAccountOnRedirect = false,
    showConnectionSnackbars = false,
  } = options;

  // Stabilise connections reference so effects don't re-run on every render.
  const connectionsKey = JSON.stringify(
    connections.map((c) => `${c.provider}:${c.accountUsername}`),
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableConnections = useMemo(() => connections, [connectionsKey]);
  const hasConnections = stableConnections.length > 0;

  // ── state ───────────────────────────────────────────────────────────
  const [events, setEvents] = useState<T[]>([]);
  const [connectionStatuses, setConnectionStatuses] =
    useState<CalendarConnectionStatusMap>({});
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedConnectionsKey, setLoadedConnectionsKey] = useState<
    string | null
  >(null);
  const [viewedMonth, setViewedMonth] = useState<Date>(() =>
    dayjs().startOf("month").toDate(),
  );
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  // True when we're still processing an Outlook OAuth redirect. Initialized
  // from sessionStorage at render time (before effects consume it) via a lazy
  // initializer so it is captured before the redirect effect deletes the keys.
  // While true the initial event fetch is skipped, preventing a flash of empty
  // state before the redirect completes and refreshInvoker() adds the connection.
  const [redirectPending, setRedirectPending] = useState(
    () => hasPendingCalendarConnect(),
  );
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // ── refs ────────────────────────────────────────────────────────────
  const loadedRangeRef = useRef<DateRange | null>(null);
  const searchRangeLoadedRef = useRef(false);
  // Prevents double-execution of the Outlook redirect-completion effect in
  // React StrictMode (which double-invokes effects on mount). The sessionStorage
  // keys are consumed on the first run; the ref ensures the second run is a
  // no-op and that connectLoading is always reset.
  const redirectHandledRef = useRef(false);
  const searchRangeFetchPromiseRef = useRef<Promise<void> | null>(null);
  const searchRunIdRef = useRef(0);
  const eventsRef = useRef<T[]>([]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  // Reset search range cache when searchPastDays changes (e.g. user toggles
  // "include past" switch), so the next search re-fetches the wider range.
  const prevSearchPastDaysRef = useRef(searchPastDays);
  useEffect(() => {
    if (prevSearchPastDaysRef.current !== searchPastDays) {
      prevSearchPastDaysRef.current = searchPastDays;
      searchRangeLoadedRef.current = false;
      searchRangeFetchPromiseRef.current = null;
    }
  }, [searchPastDays]);

  // Auto-dismiss connect error so it doesn't linger forever.
  useEffect(() => {
    if (!connectError || connectLoading) return;
    const timeoutId = window.setTimeout(() => setConnectError(null), 6000);
    return () => window.clearTimeout(timeoutId);
  }, [connectError, connectLoading]);

  const closeSnackbar = useCallback(
    () => setSnackbar((s) => ({ ...s, open: false })),
    [],
  );

  // ── helpers ────────────────────────────────────────────────────────
  const sortEvents = useCallback(
    (items: T[]) =>
      [...items].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      ),
    [],
  );

  const mergeEvents = useCallback(
    (current: T[], incoming: T[]) => {
      const merged = new Map<string, T>();
      for (const event of current) merged.set(getEventUniqueKey(event), event);
      for (const event of incoming) merged.set(getEventUniqueKey(event), event);
      return sortEvents(Array.from(merged.values()));
    },
    [sortEvents],
  );

  const filterBySearch = useCallback(
    (source: T[], query: string): T[] => {
      const trimmed = query.trim();
      if (!trimmed) return source;
      const lower = trimmed.toLowerCase();
      return source.filter((event) => matchEvent(event, lower));
    },
    [matchEvent],
  );

  // ── fetch from all providers ──────────────────────────────────────
  const fetchEventsForRange = useCallback(
    async (range: DateRange) => {
      const allEvents: T[] = [];
      const unavailableOutlookAccounts: string[] = [];
      const outlookAccountsInCache = getOutlookAccountsInMsalCache();

      // Seed all connections as "active"; degrade below.
      const statusMap: CalendarConnectionStatusMap = {};
      for (const conn of stableConnections) {
        statusMap[connectionStatusKey(conn.provider, conn.accountUsername)] =
          "active";
      }

      const connectionsToFetch = stableConnections.filter((conn) => {
        if (
          conn.provider === CalendarProvider.Outlook &&
          conn.accountUsername
        ) {
          const inCache = outlookAccountsInCache.has(
            normalizeEmail(conn.accountUsername),
          );
          if (!inCache) {
            unavailableOutlookAccounts.push(conn.accountUsername);
            statusMap[
              connectionStatusKey(conn.provider, conn.accountUsername)
            ] = "unavailable";
          }
          return inCache;
        }
        return true;
      });

      const results = await Promise.allSettled(
        connectionsToFetch.map(async (conn) => {
          if (
            conn.provider === CalendarProvider.Outlook &&
            conn.accountUsername
          ) {
            const raw = await fetchOutlookCalendarEvents(
              conn.accountUsername,
              range.startDateTime,
              range.endDateTime,
            );
            const includePrivate = conn.includePrivateEvents ?? true;
            const visible = includePrivate
              ? raw
              : raw.filter(
                  (e) =>
                    e.sensitivity !== "private" && e.sensitivity !== "personal",
                );
            return visible.map((e: unknown) =>
              mapOutlookEvent(e, conn.accountUsername),
            );
          }
          if (
            conn.provider === CalendarProvider.Google &&
            conn.accountUsername
          ) {
            const raw = await fetchGoogleCalendarEvents(
              conn.accountUsername,
              range.startDateTime,
              range.endDateTime,
            );
            return raw.map((e: unknown) =>
              mapGoogleEvent(e, conn.accountUsername),
            );
          }
          return [] as T[];
        }),
      );

      const failedGoogleAccounts: string[] = [];
      const revokedGoogleAccounts: string[] = [];
      results.forEach((result, idx) => {
        if (result.status === "fulfilled") {
          allEvents.push(...result.value);
          return;
        }
        const conn = connectionsToFetch[idx];
        const reason =
          result.reason instanceof Error
            ? result.reason.message
            : `${result.reason}`;
        if (conn.provider === CalendarProvider.Google && conn.accountUsername) {
          if (isGoogleTokenRevokedError(result.reason)) {
            revokedGoogleAccounts.push(conn.accountUsername);
            statusMap[
              connectionStatusKey(conn.provider, conn.accountUsername)
            ] = "revoked";
          } else {
            failedGoogleAccounts.push(conn.accountUsername);
            statusMap[
              connectionStatusKey(conn.provider, conn.accountUsername)
            ] = "failed";
          }
        } else if (conn.accountUsername) {
          statusMap[connectionStatusKey(conn.provider, conn.accountUsername)] =
            "failed";
        }
        console.warn(
          `[Calendar] Failed to fetch events from ${conn.provider} (${conn.accountUsername}): ${reason}`,
        );
      });

      return {
        events: sortEvents(allEvents),
        unavailableOutlookAccounts: Array.from(
          new Set(unavailableOutlookAccounts),
        ),
        failedGoogleAccounts: Array.from(new Set(failedGoogleAccounts)),
        revokedGoogleAccounts: Array.from(new Set(revokedGoogleAccounts)),
        connectionStatuses: statusMap,
      };
    },
    [stableConnections, sortEvents, mapOutlookEvent, mapGoogleEvent],
  );

  // ── warnings ───────────────────────────────────────────────────────
  const warnUnavailableOutlook = useCallback(
    (accounts: string[]) => {
      if (accounts.length === 0) return;
      setSnackbar({
        open: true,
        severity: "error",
        message: t("calendar.unavailableOutlookAccount", {
          count: accounts.length,
          accounts: accounts.join(", "),
        }),
      });
    },
    [t],
  );

  const warnFailedGoogle = useCallback(
    (accounts: string[]) => {
      if (accounts.length === 0) return;
      setSnackbar({
        open: true,
        severity: "error",
        message: t("calendar.failedGoogleAccount", {
          count: accounts.length,
          accounts: accounts.join(", "),
        }),
      });
    },
    [t],
  );

  const handleRevokedGoogleAccounts = useCallback(
    async (accounts: string[]) => {
      if (accounts.length === 0) return;
      for (const accountUsername of accounts) {
        try {
          await disconnectCalendar(CalendarProvider.Google, accountUsername);
        } catch (e) {
          console.warn(
            `[Calendar] Failed to auto-disconnect revoked Google account ${accountUsername}:`,
            e,
          );
        }
      }
      await refreshInvoker();
      setSnackbar({
        open: true,
        severity: "error",
        message: t("calendar.googleTokenRevoked", {
          count: accounts.length,
          accounts: accounts.join(", "),
        }),
      });
    },
    [t, refreshInvoker],
  );

  // ── store fetched range ───────────────────────────────────────────
  const fetchAndStoreRange = useCallback(
    async (
      range: DateRange,
      opts?: { replace?: boolean; showLoader?: boolean },
    ): Promise<{ fetchedEventsCount: number }> => {
      if (stableConnections.length === 0) {
        setEvents([]);
        setConnectionStatuses({});
        loadedRangeRef.current = null;
        searchRangeLoadedRef.current = false;
        searchRangeFetchPromiseRef.current = null;
        setLoadedConnectionsKey(connectionsKey);
        if (opts?.showLoader) setLoading(false);
        return { fetchedEventsCount: 0 };
      }

      if (opts?.showLoader) setLoading(true);

      try {
        const {
          events: fetchedEvents,
          unavailableOutlookAccounts,
          failedGoogleAccounts,
          revokedGoogleAccounts,
          connectionStatuses: newStatuses,
        } = await fetchEventsForRange(range);

        setConnectionStatuses(newStatuses);
        warnUnavailableOutlook(unavailableOutlookAccounts);
        warnFailedGoogle(failedGoogleAccounts);
        await handleRevokedGoogleAccounts(revokedGoogleAccounts);

        if (opts?.replace) {
          setEvents(fetchedEvents);
          loadedRangeRef.current = range;
          searchRangeLoadedRef.current = false;
          searchRangeFetchPromiseRef.current = null;
        } else {
          setEvents((current) => mergeEvents(current, fetchedEvents));
          loadedRangeRef.current = mergeDateRanges(
            loadedRangeRef.current,
            range,
          );
        }
        return { fetchedEventsCount: fetchedEvents.length };
      } finally {
        if (opts?.showLoader) {
          setLoadedConnectionsKey(connectionsKey);
          setLoading(false);
        }
      }
    },
    [
      connectionsKey,
      stableConnections.length,
      fetchEventsForRange,
      mergeEvents,
      warnUnavailableOutlook,
      warnFailedGoogle,
      handleRevokedGoogleAccounts,
    ],
  );

  const ensureRangeLoaded = useCallback(
    async (required: DateRange) => {
      const loaded = loadedRangeRef.current;
      if (!loaded) {
        await fetchAndStoreRange(required, { replace: true });
        return;
      }
      if (required.startDateTime.getTime() < loaded.startDateTime.getTime()) {
        await fetchAndStoreRange({
          startDateTime: required.startDateTime,
          endDateTime: new Date(loaded.startDateTime.getTime() - 1),
        });
      }
      const updated = loadedRangeRef.current;
      if (
        updated &&
        required.endDateTime.getTime() > updated.endDateTime.getTime()
      ) {
        await fetchAndStoreRange({
          startDateTime: new Date(updated.endDateTime.getTime() + 1),
          endDateTime: required.endDateTime,
        });
      }
    },
    [fetchAndStoreRange],
  );

  const monthRange = useCallback((month: Date): DateRange => {
    const start = dayjs(month).startOf("month").toDate();
    const end = dayjs(month).endOf("month").toDate();
    return { startDateTime: start, endDateTime: end };
  }, []);

  const availableMonths = useMemo<Date[]>(() => {
    const months: Date[] = [];
    const base = dayjs().startOf("month");
    for (let i = -pastMonths; i <= futureMonths; i++) {
      months.push(base.add(i, "month").toDate());
    }
    return months;
  }, [pastMonths, futureMonths]);

  const fetchAllEvents = useCallback(async () => {
    if (stableConnections.length === 0) {
      setEvents([]);
      setConnectionStatuses({});
      loadedRangeRef.current = null;
      searchRangeLoadedRef.current = false;
      searchRangeFetchPromiseRef.current = null;
      setLoadedConnectionsKey(connectionsKey);
      setLoading(false);
      return;
    }
    setConnectError(null);
    await fetchAndStoreRange(monthRange(viewedMonth), {
      replace: true,
      showLoader: true,
    });
  }, [
    connectionsKey,
    stableConnections.length,
    viewedMonth,
    monthRange,
    fetchAndStoreRange,
  ]);

  const handlePrevMonth = useCallback(() => {
    setViewedMonth((d) => dayjs(d).subtract(1, "month").startOf("month").toDate());
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewedMonth((d) => dayjs(d).add(1, "month").startOf("month").toDate());
  }, []);

  // Refetch whenever the set of connections or the viewed month changes.
  useEffect(() => {
    if (redirectPending) return;
    void fetchAllEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionsKey, redirectPending, viewedMonth]);

  // ── search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (searchText.trim().length > 0) setSearchInProgress(true);
    const timer = window.setTimeout(
      () => setDebouncedSearchText(searchText),
      DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const ensureSearchRangeLoaded = useCallback(async () => {
    if (searchRangeLoadedRef.current) return;
    if (!searchRangeFetchPromiseRef.current) {
      const searchRange: DateRange = {
        startDateTime: dayjs()
          .startOf("day")
          .subtract(searchPastDays, "day")
          .toDate(),
        endDateTime: dayjs().endOf("day").add(searchFutureDays, "day").toDate(),
      };
      searchRangeFetchPromiseRef.current = ensureRangeLoaded(searchRange)
        .then(() => {
          searchRangeLoadedRef.current = true;
        })
        .finally(() => {
          searchRangeFetchPromiseRef.current = null;
        });
    }
    await searchRangeFetchPromiseRef.current;
  }, [ensureRangeLoaded, searchPastDays, searchFutureDays]);

  useEffect(() => {
    let cancelled = false;
    const runId = ++searchRunIdRef.current;

    const run = async () => {
      const trimmed = debouncedSearchText.trim();
      if (!trimmed) {
        if (!cancelled && runId === searchRunIdRef.current) {
          setSearchInProgress(false);
        }
        return;
      }

      setSearchInProgress(true);

      try {
        await ensureSearchRangeLoaded();
        if (cancelled) return;
      } catch (error) {
        console.error("Calendar search fetch failed:", error);
      } finally {
        if (!cancelled && runId === searchRunIdRef.current) {
          setSearchInProgress(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [events, debouncedSearchText, ensureSearchRangeLoaded]);

  // ── connect / disconnect ──────────────────────────────────────────
  // Handle MSAL redirect return from Outlook connect.
  useEffect(() => {
    // Guard: useRef persists across StrictMode's simulated unmount/remount.
    // getPendingCalendarConnect() is destructive (clears sessionStorage on first
    // call), and handleRedirectPromise() processes the URL hash only once.
    // Without this guard, StrictMode's second mount sees no pending data and
    // connectLoading stays stuck at true forever.
    if (redirectHandledRef.current) return;
    const pending = getPendingCalendarConnect();
    if (!pending) return;

    redirectHandledRef.current = true;

    (async () => {
      setConnectLoading(true);
      setConnectError(null);
      try {
        const redirectResult = await msGraphAuthService.handleRedirectPromise();

        let graphToken = "";
        let calendarUsername = "";

        if (redirectResult?.accessToken && redirectResult?.account) {
          graphToken = redirectResult.accessToken;
          calendarUsername = redirectResult.account.username;
        } else {
          const allAccounts = msGraphAuthService.getAllAccounts();
          const knownSet = new Set(pending.knownAccounts);
          const calendarAccount =
            allAccounts.find((a) => !knownSet.has(a.username.toLowerCase())) ??
            allAccounts.find(
              (a) =>
                a.username.toLowerCase() !==
                pending.originalUsername.toLowerCase(),
            );
          if (!calendarAccount) {
            throw new Error(
              "Could not identify the calendar account after redirect",
            );
          }
          const result = await msGraphAuthService.getGraphToken({
            scopes: ["https://graph.microsoft.com/Calendars.Read"],
            authority: "https://login.microsoftonline.com/common",
            account: calendarAccount,
          });
          graphToken = result.accessToken;
          calendarUsername = calendarAccount.username;
        }

        if (!graphToken) {
          throw new Error("No access token received after redirect");
        }

        await completeCalendarConnect(
          pending.provider,
          graphToken,
          calendarUsername,
          pending.includePrivateEvents,
        );

        if (restoreOriginalAccountOnRedirect) {
          const original = msGraphAuthService
            .getAllAccounts()
            .find(
              (a) =>
                a.username.toLowerCase() ===
                pending.originalUsername.toLowerCase(),
            );
          if (original) msGraphAuthService.setActiveAccount(original);
        }

        await refreshInvoker();
        if (showConnectionSnackbars) {
          setSnackbar({
            open: true,
            message: t("calendar.connectSuccess"),
            severity: "success",
          });
        }
      } catch (e) {
        console.error("Calendar redirect connect failed:", e);
        const msg = getCalendarErrorMessage(e, t);
        setConnectError(msg);
        setSnackbar({ open: true, message: msg, severity: "error" });
      } finally {
        // Always reset loading — no cancelled guard needed because the ref
        // prevents this async from running more than once. In React 18,
        // setState on an unmounted component is a safe no-op.
        setConnectLoading(false);
        // Clear the redirect-pending flag so the [connectionsKey] effect can
        // run. On success connectionsKey will also have changed (new connection
        // added by refreshInvoker), triggering an event fetch. On error the
        // effect re-runs with the same empty connectionsKey and immediately
        // transitions to the correct empty state.
        setRedirectPending(false);
      }
    })();

    // No cleanup function: the ref prevents re-execution, so there is nothing
    // to cancel. Removing the cleanup also means StrictMode's simulated unmount
    // does not set a cancelled flag that would suppress setConnectLoading(false).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectCalendar = useCallback(
    async (provider: CalendarProvider, includePrivateEvents: boolean): Promise<boolean> => {
      setConnectLoading(true);
      setConnectError(null);
      try {
        const result = await connectCalendar(provider, includePrivateEvents);
        // Google returns immediately (popup); Outlook redirects the page.
        if (result) {
          await refreshInvoker();
          if (showConnectionSnackbars) {
            setSnackbar({
              open: true,
              message: t("calendar.connectSuccess"),
              severity: "success",
            });
          }
          setConnectLoading(false);
        }
        return true;
      } catch (e) {
        const err = e as { message?: string; type?: string };
        const msg = (err?.message || "").toLowerCase();
        const type = (err?.type || "").toLowerCase();
        if (msg.includes("popup") || type.includes("popup")) {
          // User closed the popup — not a real error.
          setConnectLoading(false);
          return false;
        }
        console.error("Calendar connect failed:", e);
        setConnectError(getCalendarErrorMessage(e, t));
        setConnectLoading(false);
        return false;
      }
    },
    [t, refreshInvoker, showConnectionSnackbars],
  );

  const handleDisconnectAccount = useCallback(
    async (provider: string, accountUsername: string) => {
      setConnectLoading(true);
      try {
        await disconnectCalendar(provider, accountUsername);
        await refreshInvoker();
        if (showConnectionSnackbars) {
          setSnackbar({
            open: true,
            message: t("calendar.disconnectSuccess"),
            severity: "success",
          });
        }
      } catch (e) {
        console.error("Calendar disconnect failed:", e);
        if (showConnectionSnackbars) {
          setSnackbar({
            open: true,
            message: t("calendar.disconnectError"),
            severity: "error",
          });
        } else {
          const err = e as { message?: string };
          setConnectError(err?.message || "Calendar disconnect failed");
        }
      } finally {
        setConnectLoading(false);
      }
    },
    [t, refreshInvoker, showConnectionSnackbars],
  );

  const isSearchActive = debouncedSearchText.trim().length > 0;

  // When there are no connections, clear derived state.
  useEffect(() => {
    if (!hasConnections) {
      setEvents([]);
      setConnectionStatuses({});
      loadedRangeRef.current = null;
      searchRangeLoadedRef.current = false;
      searchRangeFetchPromiseRef.current = null;
      setLoadedConnectionsKey(connectionsKey);
      setLoading(false);
    }
  }, [connectionsKey, hasConnections]);

  const filteredEvents = useMemo(
    () => filterBySearch(events, debouncedSearchText),
    [events, debouncedSearchText, filterBySearch],
  );

  return {
    events,
    filteredEvents,
    connectionStatuses,
    searchText,
    setSearchText,
    debouncedSearchText,
    isSearchActive,
    searchInProgress,
    loading:
      loading ||
      redirectPending ||
      (hasConnections && loadedConnectionsKey !== connectionsKey),
    viewedMonth,
    setViewedMonth,
    handlePrevMonth,
    handleNextMonth,
    availableMonths,
    connectLoading,
    connectError,
    setConnectError,
    handleConnectCalendar,
    handleDisconnectAccount,
    fetchAllEvents,
    snackbar,
    setSnackbar,
    closeSnackbar,
  };
}
